import { createSelector } from '@ngrx/store';
import { AutoPayVersionType } from '@arc/unified-payments-frontend/shared-payment';
import { PaymentRuleStore } from '../payment-rules';
import {
  selectIsBuyerOriginApp,
  selectIsUnauthenticatedPaymentLinksUser,
  selectSessionPaymentProcess,
} from '../session-store';
import { selectHasEligibleAutopayAccounts } from '../btpay';
import { selectHasAllowedPaymentAccounts } from './payment-accounts.selectors';

// Tenant payment-rules check for the checkout-confirmation Autopay enrollment prompt (BTPAY-7160).
// Extracted from make-payment-international-container.component.ts's inline paymentRuleSetup()
// so POI (make-payment) and POA (pay-on-account-domestic) can reuse the same rule POI-Intl already applies.
// Per architect review, this must require Enhanced Batched Autopay specifically (autoPayVersion
// === Batched) — Single (per-invoice Due/Receipt/Discount timing) is a distinct, non-batched
// scheduling mechanism and isn't in scope for the checkout-triggered enrollment prompt, even
// though Wallet's own self-enrollment UI supports both. Deliberately does NOT check
// autoPayAcceptBank — cards are also accepted for autopay (at least outside intl), so gating on
// the bank-specific rule wrongly excluded card-only tenants. Only enabled for the Buyer Flow
// (isBuyerOriginApp) — this prompt is buyer self-enrollment, not something to surface to internal
// staff paying on a buyer's behalf via ClientConnect/Collections/etc.
export const selectIsAutopaySettingEnabled = createSelector(
  PaymentRuleStore,
  selectIsBuyerOriginApp,
  (paymentRule, isBuyerOriginApp) =>
    !!isBuyerOriginApp &&
    paymentRule?.settings?.enhancedAutopaySettings?.autoPayVersion ===
      AutoPayVersionType.Batched,
);

// Payment-method eligibility, Stripe half (BTPAY-7160): Stripe payments never show the
// enrollment prompt. The Canadian one-time-PAD half of this condition is checked separately
// per flow via each flow's own selected-payment-account selector (isOneTimePaymentAccount),
// since that state lives in make-payment-core/pay-on-account, not in shared-stores.
export const selectIsAutopayEligiblePaymentProcess = createSelector(
  selectSessionPaymentProcess,
  (paymentProcess) => paymentProcess !== 'Stripe',
);

// Signed-in eligibility (BTPAY-7160): per CLAUDE.md, Payment Links are in scope only when the
// buyer is signed in — anonymous Payment Links checkouts are out of scope (Wallet enrollment
// needs an authenticated ExternalUserUrn). Portal/eIC origins are always authenticated already,
// so this only actually excludes anonymous Payment Links sessions. Reuses the existing
// selectIsUnauthenticatedPaymentLinksUser selector (session-store) that other auth-dependent
// behavior already gates on (authorization-verbiage.selectors.ts, pay-on-account.selectors.ts).
export const selectIsAutopayEligibleSignInState = createSelector(
  selectIsUnauthenticatedPaymentLinksUser,
  (isUnauthenticatedPaymentLinksUser) => !isUnauthenticatedPaymentLinksUser,
);

// BTPAY-7160: the three shared/app-wide conditions gating whether a flow should even
// dispatch its checkAutopayEligibility trigger — tenant rules, payment method, and sign-in
// state. Used by each flow's own *AutopayEligibilityDispatchEffects alongside its flow-local
// one-time-payment-account check and account number(s) for the API call.
// Deliberately NOT combined with selectHasEligibleAutopayAccounts (see
// selectShowAutopayBannerBase below) — that flag is only populated once the eligibility
// check this selector gates has already succeeded, so using it here would be circular.
export const selectIsAutopayEligibilityCheckAllowed = createSelector(
  selectIsAutopaySettingEnabled,
  selectIsAutopayEligiblePaymentProcess,
  selectIsAutopayEligibleSignInState,
  (isAutopaySettingEnabled, isEligiblePaymentProcess, isEligibleSignInState) =>
    !!isAutopaySettingEnabled &&
    !!isEligiblePaymentProcess &&
    !!isEligibleSignInState,
);

// The four BTPAY-7160 gate conditions that are genuinely shared/app-wide state: tenant
// rules, payment method, sign-in state, and whether the per-account eligibility check
// (dispatched by each flow's own container) found any unenrolled account. One-time-PAD
// (flow-local selected payment account) is deliberately NOT part of this selector — each
// flow's own selectShowAutopayEnrollmentBanner selector ANDs it in instead.
export const selectShowAutopayBannerBase = createSelector(
  selectIsAutopaySettingEnabled,
  selectIsAutopayEligiblePaymentProcess,
  selectHasEligibleAutopayAccounts,
  selectIsAutopayEligibleSignInState,
  (
    isAutopaySettingEnabled,
    isEligiblePaymentProcess,
    hasEligibleAccounts,
    isEligibleSignInState,
  ) =>
    !!isAutopaySettingEnabled &&
    !!isEligiblePaymentProcess &&
    !!hasEligibleAccounts &&
    !!isEligibleSignInState,
);

// BTPAY-7163: gate for *dispatching* the per-account eligibility check when the buyer lands on
// the Select Payment Method step (Add Payment Method modal's Autopay suggestion banner).
// Deliberately just tenant-rules + no-saved-account + sign-in state — not the full
// selectIsAutopayEligibilityCheckAllowed used at Confirmation (payment process) — per PO: if
// there's no saved account to suggest enabling Autopay for, it's not worth the API call.
// selectIsAutopayEligibleSignInState is also included, same as the Confirmation-step gate —
// anonymous Payment Links buyers can't complete wallet enrollment, so it's not worth the API call.
export const selectIsAutopaySuggestionEligibilityCheckAllowed = createSelector(
  selectIsAutopaySettingEnabled,
  selectHasAllowedPaymentAccounts,
  selectIsAutopayEligibleSignInState,
  (isAutopaySettingEnabled, hasPaymentAccounts, isEligibleSignInState) =>
    !!isAutopaySettingEnabled &&
    !hasPaymentAccounts &&
    !!isEligibleSignInState,
);

// BTPAY-7163: component-facing render gate for the Add Payment Method modal's Autopay
// suggestion banner — shown only the first time a buyer adds a payment method (no saved
// account yet), for a tenant on Enhanced Batched Autopay, when the account(s) aren't already
// enrolled, and the buyer is signed in (same sign-in exclusion as the Confirmation-step banner —
// an anonymous Payment Links buyer can't complete enrollment).
export const selectShowAutopaySuggestionBanner = createSelector(
  selectIsAutopaySettingEnabled,
  selectHasAllowedPaymentAccounts,
  selectHasEligibleAutopayAccounts,
  selectIsAutopayEligibleSignInState,
  (
    isAutopaySettingEnabled,
    hasPaymentAccounts,
    hasEligibleAccounts,
    isEligibleSignInState,
  ) =>
    !!isAutopaySettingEnabled &&
    !hasPaymentAccounts &&
    !!hasEligibleAccounts &&
    !!isEligibleSignInState,
);
